/* eslint-disable multiline-ternary */
import React, { useEffect } from 'react'
import { observer } from 'mobx-react'
import { useStores } from '../../stores/RootStore'

import { useQuery } from '@apollo/react-hooks'
import { Link, useHistory } from 'react-router-dom'
import { Alert, FormGroup, FormFeedback, Input, Label, Button, Row, Col } from 'reactstrap'
import { Form, Field } from 'react-final-form'
import { FORM_ERROR } from 'final-form'
import { EMAIL, DIGITS, UPPER, LOWER, PUNCT } from 'suredone-common'
import { Loadable, Loading } from '../../components'

import './Register.css'
import { AuthPage } from './AuthPage'
import { ScheduledEvent } from '../OnDemo/ScheduledEvent'
import { useLocalStorage, useQualifications, useQueryParams } from '../../hooks'
import { GET_QUALIFICATIONS } from '../Qualifications/commonQueries'
import { useLazyQueryAsPromise } from '../../hooks/useLazyQueryAsPromise'
import { PLAN_REDIRECT_KEY } from '../../components/RedirectToCheckout/RedirectToCheckout'

const debug = require('debug')('sd:Register')

const _Register = () => {
  const { stores: { auth } } = useStores()
  const history = useHistory()
  const { skipdemo, plan } = useQueryParams()
  const [, setStorageValue] = useLocalStorage(PLAN_REDIRECT_KEY)

  const [getUserIdFromQualifications] = useLazyQueryAsPromise(GET_QUALIFICATIONS)

  // Set on localstorage the plan to be redirected after first login
  useEffect(() => {
    if (plan) { setStorageValue(plan) }
    // eslint-disable-next-line
  }, [])

  const redirectorCallback = (storedValue) => {
    const { actionsToDo, currentChannels, wantedChannels, gmvPerMonth, ordersPerMonth, primaryCategory, skuCount } = storedValue
    const storedValueIsEmpty = storedValue && Object.keys(storedValue)?.length < 1
    const quizIsEmpty = actionsToDo?.length < 1 && currentChannels?.length < 1 && wantedChannels?.length < 1 && !gmvPerMonth && !ordersPerMonth && !primaryCategory && !skuCount
    if (!skipdemo && !plan) {
      if (storedValueIsEmpty) history.push('/q/1')
      if (!storedValueIsEmpty && quizIsEmpty) history.push('/q/1')
    }
  }
  const { storedValue, setValue } = useQualifications(redirectorCallback)
  const { firstName, lastName, email, userId, company } = storedValue

  const { data: qData, loading: qLoading, error: qError } = useQuery(GET_QUALIFICATIONS, {
    variables: { email },
    skip: !email
  })
  const { GetQualifications } = qData || {}
  const { calendlyData, firstName: qFirstName, lastName: qLastName } = GetQualifications || {}

  const onHandleSubmit = async values => {
    const { email, password, first_name: first, last_name: last, company } = values
    setValue(storedValue => ({ ...storedValue, company }))
    try {
      if (userId) {
        await auth.signUp({ email, password, first, last, userId })
      } else {
        const { data } = await getUserIdFromQualifications({ email })
        const { GetQualifications } = data || {}
        const { userId: fromQualificationsUserId } = GetQualifications || {}
        await auth.signUp({ email, password, first, last, userId: fromQualificationsUserId })
      }
    } catch (err) {
      debug({ err })
      if (window.analytics) {
        const currentEvent = 'Registration: Step1 Error'
        const currentHistory = window?.analyticsEventHistory
        const newHistory = [currentEvent, ...currentHistory].slice(0, 10)
        window.analyticsEventHistory = newHistory
        const fullFilled = {
          error: err?.message || 'User clicks register but got an error',
          pathHistory: window?.analyticsPathHistory,
          priorPath: window?.analyticsPathHistory?.[0],
          eventHistory: newHistory,
          priorEvent: newHistory?.[1]
        }
        window.analytics.track(currentEvent, fullFilled)
      }
      return { [FORM_ERROR]: err.message }
    }
    if (window.analytics) {
      const currentEvent = 'Registration: email'
      const currentHistory = window?.analyticsEventHistory
      const newHistory = [currentEvent, ...currentHistory].slice(0, 10)
      window.analyticsEventHistory = newHistory
      const fullFilled = {
        action: 'User clicks Register',
        description: 'Captures user email, first name, ...',
        pathHistory: window?.analyticsPathHistory,
        priorPath: window?.analyticsPathHistory?.[0],
        eventHistory: newHistory,
        priorEvent: newHistory?.[1]
      }
      window.analytics.track(currentEvent, fullFilled)
    }
    // Successful registration: send to happy little page
    const params = new URLSearchParams()
    if (email) params.set('email', email)
    history.push('/confirm?registration=true')
  }

  const renderForm = ({ handleSubmit, submitting, pristine, valid, submitError, values, hasValidationErrors }) => {
    return (
      <form className='custom auth-form' onSubmit={handleSubmit}>
        <Row>
          <Col xs={12} md={6} className='left-wrapper d-flex flex-wrap align-items-start border-right px-4 px-lg-5'>
            <Loadable
              inline
              loading={qLoading}
              style={{ margin: 'auto' }}
              content={calendlyData
                ? (
                  <>
                    <h6 className='mb-3 w-100'>Great, the demo was successfully scheduled!</h6>
                    <ScheduledEvent email={email} />
                  </>
                  ) : (
                    <Row>
                      <Col xs={12} className='mt-0 mb-4 d-none d-md-block'>
                        <h1>The Power of SureDone</h1>
                      </Col>

                      <Col xs={12} className='mb-4'>
                        <ul className='show-checks pl-4 ml-n1'>
                          <li className='my-2'>Consistent product data across all channels</li>
                          <li className='my-2'>Consolidates products, listings, inventory and orders</li>
                          <li className='my-2'>Reduces oversells with better inventory accuracy</li>
                          <li className='my-2'>No more managing spreadsheets or other documents</li>
                          <li className='my-2'>Leverage automation to streamline inventory and fulfillment</li>
                        </ul>
                      </Col>
                    </Row>
                  )}
            />
          </Col>

          <Col xs={12} md={6} className='right-wrapper px-4 px-lg-5'>
            <Row>
              <Col xs={12} className='mt-n2 mb-2 my-md-0 text-center'>
                <h2 className='mb-3'>Create your account</h2>
              </Col>

              {((!firstName && !qFirstName) || (!lastName && !qLastName)) && (
                <>
                  <Col xs={12} sm={6}>
                    <FormGroup>
                      <div className='form-label-group'>
                        <Field
                          name='first_name'
                          validate={event => event ? undefined : 'First name is required'}
                        >
                          {({ input, meta }) => (
                            <>
                              <Input
                                {...input}
                                autoFocus={!input.value}
                                type='text'
                                id='first_name'
                                placeholder='First name'
                                valid={meta.valid}
                                invalid={meta.error && meta.touched && !meta.pristine}
                              />
                              <Label for='first_name'>First name</Label>
                              {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                            </>
                          )}
                        </Field>
                      </div>
                    </FormGroup>
                  </Col>

                  <Col xs={12} sm={6}>
                    <FormGroup>
                      <div className='form-label-group'>
                        <Field
                          name='last_name'
                          validate={event => event ? undefined : 'Last name is required'}
                        >
                          {({ input, meta }) => (
                            <>
                              <Input
                                {...input}
                                autoFocus={!input.value && values.first_name}
                                type='text'
                                id='last_name'
                                placeholder='Last name'
                                valid={meta.valid}
                                invalid={meta.error && meta.touched && !meta.pristine}
                              />
                              <Label for='last_name'>Last name</Label>
                              {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                            </>
                          )}
                        </Field>
                      </div>
                    </FormGroup>
                  </Col>
                </>
              )}

              <Col xs={12}>
                <FormGroup>
                  <div className='form-label-group'>
                    <Field
                      name='email'
                      validate={email => {
                        if (!email) return 'Email is required'
                        if (!EMAIL.test(email)) return 'Invalid Email'
                      }}
                    >
                      {({ input, meta }) => (
                        <>
                          <Input
                            {...input}
                            autoFocus={!input.value && values.first_name && values.last_name}
                            type='text'
                            id='email'
                            disabled={!!email}
                            placeholder='Email'
                            valid={meta.valid}
                            invalid={meta.error && meta.touched && !meta.pristine}
                            onChange={(e) => {
                              input.onChange((e.target.value).toLowerCase().trim())
                            }}
                          />
                          <Label for='email'>Email</Label>
                          {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                        </>
                      )}
                    </Field>
                  </div>
                </FormGroup>
              </Col>

              <Col xs={12}>
                <FormGroup>
                  <div className='form-label-group'>
                    <Field
                      name='company'
                      validate={company => {
                        if (!company) return 'Company name is required'
                      }}
                    >
                      {({ input, meta }) => (
                        <>
                          <Input
                            {...input}
                            type='text'
                            id='company'
                            valid={meta.valid}
                            placeholder='Company name'
                            invalid={meta.error && meta.touched && !meta.pristine}
                            autoFocus={!input.value && values.first_name && values.last_name && values.email}
                          />
                          <Label for='company'>Company name</Label>
                          {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                        </>
                      )}
                    </Field>
                  </div>
                </FormGroup>
              </Col>

              <Col xs={12} sm={6}>
                <FormGroup>
                  <div className='form-label-group'>
                    <Field
                      name='password'
                      validate={password => {
                        if (!password) return 'Password is required'
                        if (!DIGITS.test(password)) return 'Password must contain one or more digits.'
                        if (!UPPER.test(password)) return 'Password must contain one or more uppercase letters.'
                        if (!LOWER.test(password)) return 'Password must contain one or more lowercase letters.'
                        if (!PUNCT.test(password)) return 'Password must contain one or more punctuation characters.'
                      }}
                    >
                      {({ input, meta }) => (
                        <>
                          <Input
                            {...input}
                            autoFocus={!input.value && values.first_name && values.last_name && values.email && values.company}
                            type='password'
                            id='password'
                            placeholder='Password'
                            valid={meta.valid}
                            invalid={meta.error && meta.touched && !meta.pristine}
                          />
                          <Label for='password'>Password</Label>
                          {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                        </>
                      )}
                    </Field>
                  </div>
                </FormGroup>
              </Col>

              <Col xs={12} sm={6}>
                <FormGroup>
                  <div className='form-label-group'>
                    <Field
                      name='password2'
                      validate={password2 => {
                        if (!password2) return 'Password confirmation is required'
                        if (values.password !== password2) return 'Passwords don\'t match'
                      }}
                    >
                      {({ input, meta }) => (
                        <>
                          <Input
                            {...input}
                            type='password'
                            id='password2'
                            placeholder='Confirm password'
                            valid={meta.valid}
                            invalid={meta.error && meta.touched && !meta.pristine}
                          />
                          <Label for='password2'>Confirm password</Label>
                          {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                        </>
                      )}
                    </Field>
                  </div>
                </FormGroup>
              </Col>

              <Col xs={12}>
                <FormGroup>
                  <Field
                    name='terms'
                    validate={terms => terms ? undefined : 'You must accept our Terms of Service in order to create an account. We work hard to make these terms reasonable so that we can be around to provide a great product for you in the future.'}
                    type='checkbox'
                  >
                    {({ input, meta }) => (
                      <>
                        <Label
                          check
                          for='terms'
                          className='custom-control custom-checkbox mb-2'
                          style={{ cursor: 'pointer' }}
                        >
                          <Input
                            {...input}
                            id='terms'
                            type='checkbox'
                            className='custom-control-input'
                          />
                          <span className='custom-control-label'>
                            I accept the <a target='_blank' rel='noopener noreferrer' href='https://www.suredone.com/terms/'>Terms of Service</a> and <a target='_blank' rel='noopener noreferrer' href='https://www.suredone.com/privacy/'>Privacy Policy</a>.
                          </span>
                        </Label>
                        {meta.error && meta.touched && !meta.pristine && <FormFeedback className='d-block mt-0'>{meta.error}</FormFeedback>}
                      </>
                    )}
                  </Field>
                </FormGroup>
              </Col>

              <Col xs={12}>
                <FormGroup className='mb-0'>
                  {submitError
                    ? (
                      <Alert color='danger'>
                        {submitError}
                      </Alert>)
                    : null}
                  {submitting ? <Loading /> : null}
                  <Button type='submit' color='primary' disabled={submitting || pristine || hasValidationErrors} block>
                    Register
                  </Button>
                </FormGroup>
              </Col>
            </Row>
          </Col>

        </Row>
      </form>
    )
  }

  const HeaderSubtitle = () => (
    <p className='d-flex w-100 justify-content-center align-items-baseline'>Already have an account? Please <Button color='link' className='pl-1' to='/login' tag={Link}>Log In</Button></p>
  )

  return (
    <AuthPage
      headerSubtitle={<HeaderSubtitle />}
      form={
        <Loadable
          error={qError}
          loading={qLoading}
          content={
            <Form
              onSubmit={onHandleSubmit}
              render={renderForm}
              initialValues={{
                email,
                company,
                first_name: firstName || qFirstName,
                last_name: lastName || qLastName
              }}
            />
          }
        />
      }
    />
  )
}

const Register = observer(_Register)

export default Register

debug('loaded')
